import { AllBlocks, JumpLink } from '@/components/ContentBlocks/ContentBlocks';
import { ComponentBlocksFunnel, PageBlocksDynamicZone } from '@/types';

export function hasRichTextContent(richText: any): boolean {
  if (!Array.isArray(richText) || richText.length === 0) return false;

  const hasText = (node: any): boolean => {
    if (!node) return false;

    if (typeof node.text === 'string' && node.text.trim().length > 0) {
      return true;
    }

    if (Array.isArray(node.children)) {
      return node.children.some(hasText);
    }

    return false;
  };

  return richText.some(hasText);
}

/**
 * Removes custom class indicators from text (e.g., "(/mb-md/ta-center/) Title" -> "Title")
 * Supports both (/class1/class2/) and (/class1/class2) formats
 */
function stripCustomClassIndicators(text: string): string {
  return text.replace(/^\(\/([^)]+)\/?\)/, '').trimStart();
}

export function extractH2FromBody(body: any): string | null {
  if (!Array.isArray(body)) return null;

  for (const node of body) {
    // Match any heading level, not just h2 — real captured content doesn't always use h2
    // for what's visually/structurally the block's title (e.g. blocks.product-features'
    // HeaderText commonly opens with an h3), and this label is only used for jump links,
    // not rendered as a literal heading itself.
    if (node.type === 'heading') {
      const rawText = node.children?.map((c: any) => c.text).join('') ?? null;
      return rawText ? stripCustomClassIndicators(rawText) : null;
    }
  }

  return null;
}

// lib/helpers/contentHelpers.ts
export const makeBlockId = (typename: string | undefined, globalIndex: number) =>
  `block-${globalIndex + 1}-${typename ?? 'Unknown'}`;

export function extractJumpLinks(
  blocks: AllBlocks[],
  startIndex = 0,
  includeAllBlocks = false
): JumpLink[] {
  let blocksToProcess = blocks;
  let adjustedStartIndex = startIndex;

  // Unless includeAllBlocks is set, only process blocks from the first PageSeparator onward
  if (!includeAllBlocks) {
    const separatorIndex = blocks.findIndex(
      (block) => block?.__typename === 'ComponentBlocksPageSeparator'
    );
    if (separatorIndex >= 0) {
      blocksToProcess = blocks.slice(separatorIndex);
      adjustedStartIndex = startIndex + separatorIndex;
    }
  }

  const links = blocksToProcess
    .map((block, i) => {
      const id = makeBlockId(block?.__typename, adjustedStartIndex + i);
      let label: string | null = null;

      switch (block?.__typename) {
        case 'ComponentBlocksThreeColumnText':
          label =
            block?.ThreeColumnTitle ?? extractH2FromBody(block?.ThreeColumnDescription) ?? null;
          break;
        case 'ComponentBlocksTwoColumnTextImageBlock':
          label = block?.TwoColumnTitle ?? extractH2FromBody(block?.TwoColumnBlurb) ?? null;
          break;
        case 'ComponentBlocksLogosBlock':
          label = block?.Title ?? null;
          break;
        case 'ComponentBlocksFaqList':
          label = extractH2FromBody(block?.Body);
          break;
        case 'ComponentBlocksStepsList':
          label = block?.Title ?? extractH2FromBody(block?.StepsListBlurb) ?? null;
          break;
        case 'ComponentBlocksText':
          label = extractH2FromBody(block?.Body);
          break;
        case 'ComponentBlocksPostcard':
          label = block?.Title ?? null;
          break;
        case 'ComponentBlocksHighlightBlock':
          label = extractH2FromBody(block?.Text);
          break;
        case 'ComponentBlocksProsAndConsTable':
          label = block?.Title ?? null;
          break;
        case 'ComponentBlocksTableWidget':
          label = block?.TableTitle ?? null;
          break;
        case 'ComponentBlocksProductFeatures':
          label = extractH2FromBody(block?.HeaderText);
          break;
        case 'ComponentBlocksAdditionalFeatures':
          label = block?.Title ?? null;
          break;
        case 'ComponentBlocksLenderList':
          label = block?.TitleText ?? null;
          break;
        case 'ComponentBlocksLoanCalculators':
          label = block?.CustomCalculatorTitle ?? null;
          break;
        case 'ComponentBlocksCalculatorGrid':
          label = block?.CalculatorTitle ?? null;
          break;
        case 'ComponentLoanLoanHighlight':
          label =
            block?.LoanHighlightTitle ?? extractH2FromBody(block?.LoanHighlightDescription) ?? null;
          break;
        case 'ComponentCalculatorCalculatorHighlight':
          label = block?.CalcHighlightTitle ?? null;
          break;
        case 'ComponentBlocksThreeSteps':
          // ThreeStepTitle only renders when ThreeStepBlurb is empty (see ThreeSteps.tsx),
          // so fall back to the blurb's heading the same way it does on screen.
          label = block?.ThreeStepTitle ?? extractH2FromBody(block?.ThreeStepBlurb) ?? null;
          break;
        case 'ComponentLoanLoanComparison':
          label = block?.LoanComparisonTitle ?? extractH2FromBody(block?.Description) ?? null;
          break;
        case 'ComponentBlocksPageSeparator':
          label = block?.SeparatorTitle ?? null;
          break;
        case 'ComponentBlocksInfoTilesBlock':
          label = extractH2FromBody(block?.InfoTilesIntro) ?? null;
          break;
        case 'ComponentBlocksLatestResourcesBlock':
          label = block?.Title ?? null;
          break;
        case 'ComponentBlocksProsAndConsTableV2':
          label = block?.ProsAndConsTitle ?? null;
          break;
        case 'ComponentBlocksCta':
          label = extractH2FromBody(block?.Body) ?? null;
          break;
        case 'ComponentBlocksFunnel':
          label = block?.FunnelHeader ?? null;
          break;
        case 'ComponentBlocksMetricHighlight':
          label = block?.MetricHighlightTitle ?? null;
          break;
        case 'ComponentBlocksImage':
          label = block?.ImageCaption ?? null;
          break;
        case 'ComponentBlocksPlatformReviewsBlock':
          label = extractH2FromBody(block?.ReviewsIntro) ?? null;
          break;
      }

      // Strip custom class indicators from the label before returning
      const cleanLabel = label ? stripCustomClassIndicators(label) : null;

      return cleanLabel ? { id, label: cleanLabel } : null;
    })
    .filter((x): x is JumpLink => !!x);

  return links;
}

export type { JumpLink };

export function findLastFunnel(blocks: Array<PageBlocksDynamicZone>): ComponentBlocksFunnel | null {
  return (
    (blocks.findLast(
      ({ __typename }) => __typename === 'ComponentBlocksFunnel'
    ) as ComponentBlocksFunnel) ?? null
  );
}
